import type {
  GanttV5Task,
  GanttV5Tick,
  GanttV5TimeRange,
  GanttV5TimeScale,
  GanttV5ViewMode,
} from './types';

export function inferGanttV5TimeRange(
  tasks: GanttV5Task[],
  paddingHours = 12,
): GanttV5TimeRange {
  if (tasks.length === 0) return { start: 0, end: 24 };
  let start = Number.POSITIVE_INFINITY;
  let end = Number.NEGATIVE_INFINITY;
  for (const task of tasks) {
    if (Number.isFinite(task.start)) start = Math.min(start, task.start);
    if (Number.isFinite(task.end)) end = Math.max(end, task.end);
  }
  if (!Number.isFinite(start) || !Number.isFinite(end)) return { start: 0, end: 24 };
  const paddedStart = Math.floor((start - paddingHours) / 24) * 24;
  const paddedEnd = Math.ceil((end + paddingHours) / 24) * 24;
  return { start: paddedStart, end: Math.max(paddedStart + 24, paddedEnd) };
}
export interface CreateGanttV5TimeScaleInput extends GanttV5TimeRange {
  pixelsPerHour: number;
  minimumWidth?: number;
  tasks?: GanttV5Task[];
  compressEmptyNights?: boolean;
  compressedPixelsPerHourRatio?: number;
}

const NIGHT_START_HOUR = 20;
const NIGHT_END_HOUR = 30;

const buildEmptyNightIntervals = (
  start: number,
  end: number,
  tasks: GanttV5Task[],
): GanttV5TimeRange[] => {
  const intervals: GanttV5TimeRange[] = [];
  const firstDay = Math.floor((start - NIGHT_END_HOUR) / 24);
  const lastDay = Math.ceil((end - NIGHT_START_HOUR) / 24);
  for (let day = firstDay; day <= lastDay; day += 1) {
    const intervalStart = Math.max(start, day * 24 + NIGHT_START_HOUR);
    const intervalEnd = Math.min(end, day * 24 + NIGHT_END_HOUR);
    if (intervalEnd <= intervalStart) continue;
    const occupied = tasks.some((task) => (
      Number.isFinite(task.start)
      && Number.isFinite(task.end)
      && task.start < intervalEnd
      && task.end > intervalStart
    ));
    if (!occupied) intervals.push({ start: intervalStart, end: intervalEnd });
  }
  return intervals;
};

interface ScaleSegment extends GanttV5TimeRange {
  startX: number;
  endX: number;
  pixelsPerHour: number;
}

const buildScaleSegments = (
  start: number,
  end: number,
  pixelsPerHour: number,
  compressedIntervals: GanttV5TimeRange[],
  compressedRatio: number,
): ScaleSegment[] => {
  const boundaries = new Set<number>([start, end]);
  for (const interval of compressedIntervals) {
    boundaries.add(interval.start);
    boundaries.add(interval.end);
  }
  const hours = Array.from(boundaries).sort((left, right) => left - right);
  const segments: ScaleSegment[] = [];
  let x = 0;
  for (let index = 0; index < hours.length - 1; index += 1) {
    const segmentStart = hours[index];
    const segmentEnd = hours[index + 1];
    const compressed = compressedIntervals.some((interval) => (
      segmentStart >= interval.start && segmentEnd <= interval.end
    ));
    const segmentPixelsPerHour = pixelsPerHour * (compressed ? compressedRatio : 1);
    const endX = x + (segmentEnd - segmentStart) * segmentPixelsPerHour;
    segments.push({
      start: segmentStart,
      end: segmentEnd,
      startX: x,
      endX,
      pixelsPerHour: segmentPixelsPerHour,
    });
    x = endX;
  }
  return segments;
};

export function createGanttV5TimeScale({
  start,
  end,
  pixelsPerHour,
  minimumWidth = 1,
  tasks = [],
  compressEmptyNights = false,
  compressedPixelsPerHourRatio = 0.2,
}: CreateGanttV5TimeScaleInput): GanttV5TimeScale {
  const safeStart = Number.isFinite(start) ? start : 0;
  const safeEnd = Number.isFinite(end) && end > safeStart ? end : safeStart + 24;
  const safePixelsPerHour = Math.max(0.01, pixelsPerHour);
  const safeCompressedRatio = Math.max(0.05, Math.min(1, compressedPixelsPerHourRatio));
  const compressedIntervals = compressEmptyNights
    ? buildEmptyNightIntervals(safeStart, safeEnd, tasks)
    : [];
  const segments = buildScaleSegments(
    safeStart,
    safeEnd,
    safePixelsPerHour,
    compressedIntervals,
    safeCompressedRatio,
  );
  const calculatedWidth = segments.at(-1)?.endX ?? 0;

  const hourToX = (hour: number): number => {
    if (hour <= safeStart) return (hour - safeStart) * segments[0].pixelsPerHour;
    if (hour >= safeEnd) {
      const last = segments[segments.length - 1];
      return last.endX + (hour - safeEnd) * last.pixelsPerHour;
    }
    const segment = segments.find((item) => hour >= item.start && hour <= item.end) ?? segments[0];
    return segment.startX + (hour - segment.start) * segment.pixelsPerHour;
  };

  const xToHour = (x: number): number => {
    if (x <= 0) return safeStart + x / segments[0].pixelsPerHour;
    if (x >= calculatedWidth) {
      const last = segments[segments.length - 1];
      return safeEnd + (x - calculatedWidth) / last.pixelsPerHour;
    }
    const segment = segments.find((item) => x >= item.startX && x <= item.endX) ?? segments[0];
    return segment.start + (x - segment.startX) / segment.pixelsPerHour;
  };

  return {
    start: safeStart,
    end: safeEnd,
    pixelsPerHour: safePixelsPerHour,
    totalWidth: Math.max(minimumWidth, calculatedWidth),
    compressedIntervals,
    hourToX,
    xToHour,
    deltaPixelsToHours: (deltaPixels, anchorHour = safeStart) => (
      xToHour(hourToX(anchorHour) + deltaPixels) - anchorHour
    ),
  };
}

const getTickStep = (mode: GanttV5ViewMode, pixelsPerHour: number): number => {
  if (mode === 'hour') return pixelsPerHour >= 18 ? 1 : 3;
  if (mode === 'week') return 24 * 7;
  if (mode === 'month') return 24 * 7;
  return pixelsPerHour >= 8 ? 6 : 24;
};

const getMajorStep = (mode: GanttV5ViewMode): number => {
  if (mode === 'hour') return 24;
  if (mode === 'week' || mode === 'month') return 24 * 7;
  return 24;
};

const formatTick = (
  hour: number,
  mode: GanttV5ViewMode,
  timelineOriginDate?: string,
): string => {
  if (timelineOriginDate) {
    const origin = new Date(timelineOriginDate);
    if (!Number.isNaN(origin.getTime())) {
      const value = new Date(origin.getTime() + hour * 60 * 60 * 1000);
      if (mode === 'hour') {
        return new Intl.DateTimeFormat('zh-CN', {
          month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
        }).format(value);
      }
      return new Intl.DateTimeFormat('zh-CN', { month: 'numeric', day: 'numeric' }).format(value);
    }
  }
  if (mode === 'hour') {
    const day = Math.floor(hour / 24) + 1;
    const hourOfDay = ((Math.round(hour) % 24) + 24) % 24;
    return `第 ${day} 天 ${hourOfDay.toString().padStart(2, '0')}:00`;
  }
  return `第 ${Math.floor(hour / 24) + 1} 天`;
};

export function buildGanttV5Ticks(
  scale: GanttV5TimeScale,
  mode: GanttV5ViewMode,
  timelineOriginDate?: string,
): GanttV5Tick[] {
  const step = getTickStep(mode, scale.pixelsPerHour);
  const majorStep = getMajorStep(mode);
  const first = Math.floor(scale.start / step) * step;
  const ticks: GanttV5Tick[] = [];
  let lastLabelX = Number.NEGATIVE_INFINITY;
  for (let hour = first; hour <= scale.end; hour += step) {
    if (hour < scale.start) continue;
    const major = Math.abs(hour % majorStep) < 0.001;
    const x = scale.hourToX(hour);
    const wantsLabel = major || mode === 'hour';
    const hasLabelSpace = x - lastLabelX >= 44;
    const label = wantsLabel && hasLabelSpace ? formatTick(hour, mode, timelineOriginDate) : undefined;
    if (label) lastLabelX = x;
    ticks.push({
      hour,
      x,
      major,
      label,
    });
  }
  return ticks;
}

export function clampGanttV5Zoom(value: number, minimum = 1.5, maximum = 48): number {
  return Math.max(minimum, Math.min(maximum, value));
}

export function getGanttV5FitPixelsPerHour(
  range: GanttV5TimeRange,
  viewportWidth: number,
  minimum = 1.5,
  maximum = 48,
): number {
  const duration = Math.max(1, range.end - range.start);
  return clampGanttV5Zoom(Math.max(1, viewportWidth) / duration, minimum, maximum);
}
